import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Trash2, SlidersHorizontal, Loader2, Filter, Info, Check, Sparkles, FlaskConical, X, ChevronDown, ChevronUp, CheckCircle2, XCircle, Code2, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog as CodeDialog, DialogContent as CodeDialogContent, DialogHeader as CodeDialogHeader, DialogTitle as CodeDialogTitle, DialogDescription as CodeDialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/* ─────────────────────────  P1.5 — UX helpers  ───────────────────────── */

// Plain-English explainer for every operator. Surfaced in tooltips inside the
// dropdown and on hover anywhere we render an op label. Keeps the buyer from
// guessing what "in" vs "equals" means.
const OP_HELP = {
  equals:     "Exact match. Lead's value must equal this.",
  not_equals: "Must NOT equal this. Useful for excluding a single bad source.",
  in:         "Lead's value must be one of these (e.g. country in [GB, IE]).",
  not_in:     "Lead's value must NOT be in this list.",
  gte:        "Greater than or equal to (numbers).",
  lte:        "Less than or equal to (numbers).",
  gt:         "Strictly greater than (numbers).",
  lt:         "Strictly less than (numbers).",
  exists:     "Field must be present on the lead (any non-empty value).",
  not_exists: "Field must be absent or empty on the lead.",
};

// Sector-aware rule templates. Sectors=null means universal; otherwise only
// shown for those sector slugs. The buyer picks one and we drop the rules
// into the draft — fastest path from blank page to a working filter.
const RULE_TEMPLATES = [
  {
    id: "uk-only", label: "UK only", icon: "🇬🇧",
    summary: "country = GB",
    rules: [{ field: "country", op: "equals", value: "GB" }],
  },
  {
    id: "age-25-65", label: "Age 25–65", icon: "📅",
    summary: "age ≥ 25 AND ≤ 65",
    rules: [
      { field: "age", op: "gte", value: 25 },
      { field: "age", op: "lte", value: 65 },
    ],
  },
  {
    id: "loan-10k", label: "Loan ≥ £10k", icon: "💷",
    summary: "loan_amount ≥ 10000",
    sectors: ["mortgages", "business-finance", "commercial-finance"],
    rules: [{ field: "loan_amount", op: "gte", value: 10000 }],
  },
  {
    id: "income-25k", label: "Income ≥ £25k", icon: "💼",
    summary: "annual_income ≥ 25000",
    rules: [{ field: "annual_income", op: "gte", value: 25000 }],
  },
  {
    id: "employed", label: "Employed (PAYE)", icon: "🧾",
    summary: "employment_status = employed",
    rules: [{ field: "employment_status", op: "equals", value: "employed" }],
  },
  {
    id: "homeowners", label: "Homeowners", icon: "🏠",
    summary: "homeownership = owner",
    sectors: ["mortgages", "life-insurance", "wealth-management"],
    rules: [{ field: "homeownership", op: "equals", value: "owner" }],
  },
  {
    id: "email-phone", label: "Email + phone", icon: "✉️",
    summary: "email exists AND phone exists",
    rules: [
      { field: "email", op: "exists" },
      { field: "phone", op: "exists" },
    ],
  },
];

// Client-side evaluator — mirrors backend `_evaluate_criterion` in criteria.py
// so the buyer sees the SAME pass/fail logic the routing engine will apply.
// Kept tiny on purpose; if the backend logic ever drifts, replay covers it.
function evalRule(rule, payload) {
  if (!rule || !rule.field || !rule.op) return { pass: false, reason: "incomplete rule" };
  const lookup = (key) => {
    if (!payload || typeof payload !== "object") return undefined;
    if (key in payload) return payload[key];
    if (payload.extra_fields && typeof payload.extra_fields === "object" && key in payload.extra_fields) {
      return payload.extra_fields[key];
    }
    return undefined;
  };
  const v = lookup(rule.field);
  const target = rule.value;
  const norm = (x) => (typeof x === "string" ? x.toLowerCase().trim() : x);
  const num = (x) => (x === null || x === undefined || x === "" || Number.isNaN(Number(x)) ? null : Number(x));

  switch (rule.op) {
    case "exists":     return { pass: v !== undefined && v !== null && v !== "" };
    case "not_exists": return { pass: v === undefined || v === null || v === "" };
    case "equals":     return { pass: norm(v) === norm(target) };
    case "not_equals": return { pass: norm(v) !== norm(target) };
    case "in":         return { pass: Array.isArray(target) && target.map(norm).includes(norm(v)) };
    case "not_in":     return { pass: Array.isArray(target) && !target.map(norm).includes(norm(v)) };
    case "gte": { const nv = num(v), nt = num(target); return { pass: nv !== null && nt !== null && nv >= nt }; }
    case "lte": { const nv = num(v), nt = num(target); return { pass: nv !== null && nt !== null && nv <= nt }; }
    case "gt":  { const nv = num(v), nt = num(target); return { pass: nv !== null && nt !== null && nv >  nt }; }
    case "lt":  { const nv = num(v), nt = num(target); return { pass: nv !== null && nt !== null && nv <  nt }; }
    default:           return { pass: false, reason: `unsupported op: ${rule.op}` };
  }
}

const SAMPLE_PAYLOAD = {
  sector: "mortgages",
  first_name: "Olivia",
  last_name: "Hughes",
  email: "olivia.hughes@example.com",
  phone: "+447900654321",
  country: "GB",
  city: "Manchester",
  postcode: "M1 2AB",
  extra_fields: {
    age: 36,
    annual_income: 48000,
    loan_amount: 185000,
    employment_status: "employed",
    homeownership: "owner",
  },
};

/**
 * CriteriaBuilder — the heart of the per-campaign criteria engine.
 *
 * Designed to be obvious to a non-technical buyer: pick a field, pick an
 * operator, type a value. The UI auto-filters operators by field type
 * (number fields don't show "is one of"; enum fields show a dropdown of
 * allowed values; boolean fields show True/False switch).
 *
 * Render style: inline list of "human-readable" pills + an Edit button that
 * opens a modal builder. Same component used on /admin/campaigns and
 * /dashboard/campaign.
 *
 * Props:
 *  - value:      array of {field, op, value}
 *  - onSave:     async (next) => void   — called when modal Save is clicked
 *  - testid:     string suffix for data-testid
 *  - readOnly:   if true, hides the Edit button (rendering chips only)
 */
export default function CriteriaBuilder({ value = [], onSave, testid = "campaign", readOnly = false, sector = null }) {
  const [schema, setSchema] = useState(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || []);
  const [saving, setSaving] = useState(false);

  // P1.5 — live test panel state
  const [testOpen, setTestOpen] = useState(false);
  const [sampleText, setSampleText] = useState(() => JSON.stringify(SAMPLE_PAYLOAD, null, 2));
  const [sampleParsed, setSampleParsed] = useState(SAMPLE_PAYLOAD);
  const [sampleErr, setSampleErr] = useState(null);

  // P1.5 — first-visit welcome banner
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return !localStorage.getItem("connexis.criteriaTourSeen"); } catch (_) { return true; }
  });
  const dismissWelcome = () => {
    setShowWelcome(false);
    try { localStorage.setItem("connexis.criteriaTourSeen", "1"); } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    if (open && !schema) {
      api.get("/criteria/schema").then(r => setSchema(r.data)).catch(() => toast.error("Could not load criteria fields"));
    }
  }, [open, schema]);

  useEffect(() => { setDraft(value || []); }, [JSON.stringify(value || [])]);

  // Re-parse sample on every change; show inline error rather than toasting
  useEffect(() => {
    try {
      const p = JSON.parse(sampleText);
      setSampleParsed(p && typeof p === "object" ? p : {});
      setSampleErr(null);
    } catch (e) {
      setSampleErr(e.message || "Invalid JSON");
    }
  }, [sampleText]);

  const addRule = () => setDraft(d => [...d, { field: "country", op: "equals", value: "" }]);
  const updateRule = (i, patch) => setDraft(d => d.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRule = (i) => setDraft(d => d.filter((_, idx) => idx !== i));

  const applyTemplate = (tpl) => {
    // Merge template rules into draft, de-duping on (field, op)
    setDraft(d => {
      const key = (r) => `${r.field}::${r.op}`;
      const existing = new Set((d || []).map(key));
      const additions = tpl.rules.filter(r => !existing.has(key(r)));
      return [...(d || []), ...additions];
    });
    toast.success(`Added "${tpl.label}" — ${tpl.rules.length} rule${tpl.rules.length === 1 ? "" : "s"}`);
  };

  const availableTemplates = useMemo(() => (
    RULE_TEMPLATES.filter(t => !t.sectors || (sector && t.sectors.includes(sector)))
  ), [sector]);

  // Evaluate all draft rules against the sample; everything must pass.
  const sampleResults = useMemo(() => {
    if (sampleErr) return null;
    return (draft || []).map(r => ({ rule: r, ...evalRule(r, sampleParsed) }));
  }, [draft, sampleParsed, sampleErr]);
  const allPass = sampleResults && sampleResults.length > 0 && sampleResults.every(x => x.pass);

  const save = async () => {
    // Strip empties — rules with no value (and no exists/not_exists op) are dropped
    const clean = (draft || []).filter(r => {
      if (!r.field || !r.op) return false;
      if (r.op === "exists" || r.op === "not_exists") return true;
      if (Array.isArray(r.value)) return r.value.length > 0;
      return r.value !== "" && r.value !== null && r.value !== undefined;
    });
    setSaving(true);
    try {
      await onSave?.(clean);
      setOpen(false);
      toast.success(`Criteria updated · ${clean.length} rule${clean.length === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save criteria");
    } finally {
      setSaving(false);
    }
  };

  // Read-only chip render
  return (
    <div className="space-y-2.5" data-testid={`criteria-${testid}`}>
      {value.length === 0 ? (
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <Filter className="size-3.5" />
          <span>No criteria — all matching-sector leads accepted</span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {value.map((r, i) => (
            <Badge key={i} variant="outline" className="text-[11.5px] font-medium border-slate-300 bg-white text-slate-700 py-1 px-2.5 gap-1">
              <span className="font-mono text-slate-500">{prettyField(r.field)}</span>
              <span className="text-slate-400">{prettyOp(r.op)}</span>
              <span className="font-semibold text-slate-900">{prettyValue(r.value, r.op)}</span>
            </Badge>
          ))}
        </div>
      )}

      {!readOnly && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="border-slate-200 h-8 text-xs" data-testid={`criteria-edit-btn-${testid}`}>
              <SlidersHorizontal className="size-3.5 mr-1.5" />
              {value.length === 0 ? "Add criteria" : `Edit criteria (${value.length})`}
            </Button>
          </DialogTrigger>

          <DialogContent className="max-w-3xl" data-testid={`criteria-modal-${testid}`}>
            <DialogHeader>
              <DialogTitle className="text-lg flex items-center gap-2">
                Lead criteria for this campaign
                {sector && (
                  <Badge variant="outline" className="text-[10.5px] font-mono uppercase tracking-wider border-slate-200 text-slate-600">
                    {sector}
                  </Badge>
                )}
              </DialogTitle>
              <p className="text-[12.5px] text-slate-500 mt-1 leading-snug">
                Only leads that satisfy <strong>every rule</strong> are routed to this campaign. Mismatched leads cascade to the next campaign in this sector. No criteria = accept every matching-sector lead.
              </p>
              <DialogDescription className="sr-only">
                Configure routing criteria for this campaign. Add rules with a field, operator, and value. Use starter templates or test rules against a sample lead before saving.
              </DialogDescription>
            </DialogHeader>

            {showWelcome && (
              <div
                className="relative rounded-md border border-blue-100 bg-blue-50/50 p-3.5 pr-9"
                data-testid="criteria-welcome-banner"
              >
                <button
                  onClick={dismissWelcome}
                  className="absolute top-2.5 right-2.5 size-6 rounded-md hover:bg-blue-100/70 flex items-center justify-center text-blue-700/70 hover:text-blue-900 transition-colors"
                  aria-label="Dismiss welcome"
                  data-testid="criteria-welcome-dismiss"
                >
                  <X className="size-3.5" />
                </button>
                <div className="flex gap-3">
                  <Sparkles className="size-4 text-blue-600 shrink-0 mt-0.5" />
                  <div className="text-[12.5px] text-blue-900 leading-snug">
                    <span className="font-semibold">First time here?</span> Criteria are filters that say <em>&ldquo;only send me leads where…&rdquo;</em>.
                    <ol className="mt-2 space-y-1 list-decimal list-inside text-blue-900/85">
                      <li>Pick a starter template below <em>or</em> add a rule manually.</li>
                      <li>Each rule has a <strong>field</strong> (e.g. age), an <strong>operator</strong> (e.g. ≥), and a <strong>value</strong> (e.g. 25).</li>
                      <li>Use <strong>Test rules</strong> to paste a sample lead and see exactly what passes and fails before going live.</li>
                    </ol>
                  </div>
                </div>
              </div>
            )}

            {!schema && (
              <div className="py-12 text-center text-sm text-slate-500"><Loader2 className="size-4 inline animate-spin mr-2"/> Loading fields…</div>
            )}

            {schema && (
              <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
                {/* P1.5 — sector-aware quick templates */}
                {availableTemplates.length > 0 && (
                  <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3" data-testid="criteria-templates">
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                      <Sparkles className="size-3" /> Starter templates
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {availableTemplates.map((tpl) => (
                        <button
                          key={tpl.id}
                          type="button"
                          onClick={() => applyTemplate(tpl)}
                          className="group inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-900 hover:border-slate-900 hover:text-white text-[12px] font-medium text-slate-700 px-3 py-1.5 transition-colors"
                          data-testid={`criteria-template-${tpl.id}`}
                          title={tpl.summary}
                        >
                          <span aria-hidden>{tpl.icon}</span>
                          <span>{tpl.label}</span>
                          <Plus className="size-3 opacity-50 group-hover:opacity-100" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {draft.length === 0 && (
                  <div className="rounded-md border border-slate-200 border-dashed bg-slate-50/60 p-8 text-center">
                    <Filter className="size-7 text-slate-300 mx-auto" />
                    <p className="mt-3 text-[13px] text-slate-600">No criteria set — every matching-sector lead will be accepted.</p>
                    <Button size="sm" variant="outline" className="mt-4 border-slate-200" onClick={addRule} data-testid="criteria-add-first">
                      <Plus className="size-3.5 mr-1.5"/> Add your first rule
                    </Button>
                  </div>
                )}

                {draft.map((r, i) => (
                  <RuleRow
                    key={i}
                    rule={r}
                    schema={schema}
                    onChange={(patch) => updateRule(i, patch)}
                    onRemove={() => removeRule(i)}
                    testid={`${testid}-${i}`}
                  />
                ))}

                {draft.length > 0 && (
                  <Button size="sm" variant="outline" className="border-slate-200 border-dashed w-full" onClick={addRule} data-testid="criteria-add-more">
                    <Plus className="size-3.5 mr-1.5"/> Add another rule
                  </Button>
                )}

                {/* P1.5 — live test panel */}
                {draft.length > 0 && (
                  <TestPanel
                    open={testOpen}
                    onToggle={() => setTestOpen(o => !o)}
                    sampleText={sampleText}
                    setSampleText={setSampleText}
                    sampleErr={sampleErr}
                    sampleResults={sampleResults}
                    allPass={allPass}
                  />
                )}

                <div className="rounded-md border border-slate-100 bg-blue-50/30 p-3 flex gap-2.5">
                  <Info className="size-3.5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="text-[12px] text-slate-600 leading-snug">
                    Field values come from the lead payload posted by publishers. Missing fields automatically fail rules (except <code className="text-[11px] bg-slate-100 px-1 rounded">is missing</code>). Test your rules with the <a href="/admin/replay" className="underline">replay tool</a>.
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
              <span className="text-[11.5px] text-slate-500">{draft.length} rule{draft.length === 1 ? "" : "s"} · all must pass</span>
              <div className="flex gap-2">
                {draft.length > 0 && (
                  <CopyAsCodeButton draft={draft} sector={sector} testid={testid} />
                )}
                <Button variant="ghost" onClick={() => { setDraft(value || []); setOpen(false); }} data-testid={`criteria-cancel-${testid}`}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 gap-1.5" data-testid={`criteria-save-${testid}`}>
                  {saving ? <Loader2 className="size-3.5 animate-spin"/> : <Check className="size-3.5"/>}
                  Save criteria
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function RuleRow({ rule, schema, onChange, onRemove, testid }) {
  const fieldMeta = schema.fields[rule.field];
  const opLabels = schema.operators;
  const allowedOps = fieldMeta?.operators || Object.keys(opLabels);

  // Reset op + value if the field type changes and current op isn't valid
  const setField = (field) => {
    const meta = schema.fields[field];
    const newOp = (meta?.operators || []).includes(rule.op) ? rule.op : (meta?.operators?.[0] || "equals");
    let newVal = rule.value;
    if (meta?.type !== fieldMeta?.type) newVal = (newOp === "in" || newOp === "not_in") ? [] : "";
    onChange({ field, op: newOp, value: newVal });
  };
  const setOp = (op) => {
    let v = rule.value;
    // Switching to/from array operators
    const arrayOp = (op === "in" || op === "not_in");
    const prevArray = (rule.op === "in" || rule.op === "not_in");
    if (arrayOp && !prevArray) v = (rule.value !== "" && rule.value != null) ? [rule.value] : [];
    if (!arrayOp && prevArray)  v = Array.isArray(rule.value) ? (rule.value[0] ?? "") : (rule.value ?? "");
    if (op === "exists" || op === "not_exists") v = null;
    onChange({ op, value: v });
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 flex items-center gap-2 flex-wrap" data-testid={`rule-${testid}`}>
      <span className="text-[11px] uppercase tracking-[0.16em] text-slate-400 font-mono shrink-0">IF</span>

      <Select value={rule.field} onValueChange={setField}>
        <SelectTrigger className="h-9 text-[13px] w-48" data-testid={`rule-field-${testid}`}>
          <SelectValue placeholder="Pick a field" />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {Object.entries(schema.fields).map(([key, meta]) => (
            <SelectItem key={key} value={key}>
              <div className="flex items-center gap-2">
                <span>{meta.label}</span>
                {meta.sectors && (
                  <span className="text-[9.5px] uppercase tracking-wider text-slate-400">{meta.sectors.join(",")}</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={rule.op} onValueChange={setOp}>
        <SelectTrigger className="h-9 text-[13px] w-36" data-testid={`rule-op-${testid}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {allowedOps.map(op => (
            <SelectItem key={op} value={op}>
              <span className="flex items-center justify-between gap-3 w-full">
                <span>{opLabels[op] || op}</span>
                {OP_HELP[op] && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Info className="size-3 text-slate-300 hover:text-slate-500 shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[220px] text-[11.5px]">{OP_HELP[op]}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <ValueInput rule={rule} fieldMeta={fieldMeta} onChange={(value) => onChange({ value })} testid={testid} />

      <Button size="sm" variant="ghost" className="ml-auto h-9 px-2 text-rose-600 hover:bg-rose-50" onClick={onRemove} data-testid={`rule-remove-${testid}`}>
        <Trash2 className="size-3.5" />
      </Button>
    </div>
  );
}

function ValueInput({ rule, fieldMeta, onChange, testid }) {
  if (rule.op === "exists" || rule.op === "not_exists") {
    return <span className="text-[12px] text-slate-400 italic px-2">(no value needed)</span>;
  }
  const isArray = rule.op === "in" || rule.op === "not_in";

  // Enum / boolean field with a fixed list of values
  if (fieldMeta?.type === "boolean" || (fieldMeta?.values && !isArray)) {
    return (
      <Select value={String(rule.value ?? "")} onValueChange={(v) => onChange(parseValue(v, fieldMeta))}>
        <SelectTrigger className="h-9 text-[13px] flex-1 min-w-40" data-testid={`rule-value-${testid}`}>
          <SelectValue placeholder="Pick a value" />
        </SelectTrigger>
        <SelectContent>
          {(fieldMeta.values || []).map(v => (
            <SelectItem key={String(v)} value={String(v)}>{prettyValueAtom(v)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Multi-select for 'in' / 'not_in' against enums
  if (isArray && fieldMeta?.values) {
    const arr = Array.isArray(rule.value) ? rule.value : [];
    const toggle = (v) => {
      const has = arr.map(String).includes(String(v));
      const next = has ? arr.filter(x => String(x) !== String(v)) : [...arr, v];
      onChange(next);
    };
    return (
      <div className="flex flex-wrap gap-1 flex-1 min-w-40" data-testid={`rule-value-${testid}`}>
        {fieldMeta.values.map(v => {
          const on = arr.map(String).includes(String(v));
          return (
            <button
              key={String(v)}
              type="button"
              onClick={() => toggle(v)}
              className={`px-2.5 py-1 rounded-sm text-[11.5px] font-medium border transition-colors ${
                on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {prettyValueAtom(v)}
            </button>
          );
        })}
      </div>
    );
  }

  // Free-text 'in' / 'not_in' (comma-separated)
  if (isArray) {
    return (
      <Input
        className="h-9 text-[13px] flex-1 min-w-40"
        placeholder='e.g. GB, IE'
        value={(Array.isArray(rule.value) ? rule.value : []).join(", ")}
        onChange={(e) => onChange(e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
        data-testid={`rule-value-${testid}`}
      />
    );
  }

  // Numeric
  if (fieldMeta?.type === "number") {
    return (
      <Input
        type="number"
        className="h-9 text-[13px] w-40 tabular-nums"
        placeholder={String(fieldMeta?.examples?.[0] ?? "0")}
        value={rule.value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`rule-value-${testid}`}
      />
    );
  }

  // Free-text string
  return (
    <Input
      className="h-9 text-[13px] flex-1 min-w-40"
      placeholder={fieldMeta?.examples?.[0] || "Value"}
      value={rule.value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`rule-value-${testid}`}
    />
  );
}

/* ─────────── Pretty-print helpers (for the read-only chips) ─────────── */

function prettyField(field) {
  if (!field) return "?";
  // Replace underscores, sentence-case the first word — minimal fuss
  return field.replace(/_/g, " ");
}
function prettyOp(op) {
  return ({
    equals: "is", not_equals: "is not",
    in: "in", not_in: "not in",
    gte: "≥", lte: "≤", gt: ">", lt: "<",
    exists: "exists", not_exists: "missing",
  })[op] || op;
}
function prettyValue(v, op) {
  if (op === "exists" || op === "not_exists") return "";
  if (Array.isArray(v)) return v.map(prettyValueAtom).join(", ");
  return prettyValueAtom(v);
}
function prettyValueAtom(v) {
  if (v === true)  return "true";
  if (v === false) return "false";
  if (v == null)   return "";
  return String(v);
}
function parseValue(raw, fieldMeta) {
  if (fieldMeta?.type === "boolean") return raw === "true";
  if (fieldMeta?.type === "number")  return raw === "" ? "" : Number(raw);
  return raw;
}


/* ─────────────────────────  P1.5 — Live test panel  ───────────────────────── */

function TestPanel({ open, onToggle, sampleText, setSampleText, sampleErr, sampleResults, allPass }) {
  return (
    <div className="rounded-md border border-slate-200 overflow-hidden" data-testid="criteria-test-panel">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50/60 hover:bg-slate-100/70 transition-colors text-left"
        data-testid="criteria-test-toggle"
      >
        <span className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
          <FlaskConical className="size-3.5 text-violet-500" />
          Test rules with a sample lead
          {sampleResults && sampleResults.length > 0 && (
            <Badge
              variant="outline"
              className={`text-[10.5px] font-mono uppercase tracking-wider ml-1 ${
                allPass ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                       : "border-rose-200 bg-rose-50 text-rose-700"
              }`}
            >
              {allPass ? "PASS" : "FAIL"}
            </Badge>
          )}
        </span>
        {open ? <ChevronUp className="size-3.5 text-slate-500" /> : <ChevronDown className="size-3.5 text-slate-500" />}
      </button>

      {open && (
        <div className="border-t border-slate-200 bg-white p-3 space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Sample lead (JSON)</label>
              <button
                type="button"
                onClick={() => setSampleText(JSON.stringify(SAMPLE_PAYLOAD, null, 2))}
                className="text-[11px] text-blue-600 hover:underline"
                data-testid="criteria-test-reset-sample"
              >
                Reset to default
              </button>
            </div>
            <textarea
              value={sampleText}
              onChange={(e) => setSampleText(e.target.value)}
              spellCheck={false}
              className="w-full font-mono text-[11.5px] leading-[1.55] bg-slate-950 text-slate-100 rounded p-2.5 h-44 resize-y border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              data-testid="criteria-test-sample-textarea"
            />
            {sampleErr && (
              <div className="mt-1.5 text-[11.5px] text-rose-600 font-medium" data-testid="criteria-test-json-err">
                JSON error: {sampleErr}
              </div>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">
              Result {sampleResults && `· ${sampleResults.filter(r => r.pass).length}/${sampleResults.length} passing`}
            </div>
            {(!sampleResults || sampleResults.length === 0) ? (
              <div className="text-[12px] text-slate-400 italic">No rules to test.</div>
            ) : (
              <div className="space-y-1.5" data-testid="criteria-test-results">
                {sampleResults.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 rounded border px-2.5 py-1.5 text-[12px] ${
                      r.pass ? "border-emerald-200 bg-emerald-50/50" : "border-rose-200 bg-rose-50/50"
                    }`}
                    data-testid={`criteria-test-result-${i}`}
                  >
                    {r.pass
                      ? <CheckCircle2 className="size-3.5 text-emerald-600 shrink-0" />
                      : <XCircle className="size-3.5 text-rose-600 shrink-0" />}
                    <span className="font-mono text-slate-500">{r.rule.field}</span>
                    <span className="text-slate-400">{(OP_HELP[r.rule.op] && r.rule.op) || r.rule.op}</span>
                    <span className="font-semibold text-slate-900">
                      {Array.isArray(r.rule.value) ? r.rule.value.join(", ") : String(r.rule.value ?? "")}
                    </span>
                    <span className={`ml-auto text-[11px] font-semibold ${r.pass ? "text-emerald-700" : "text-rose-700"}`}>
                      {r.pass ? "PASS" : "FAIL"}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {sampleResults && sampleResults.length > 0 && (
              <div className={`mt-2.5 text-[12px] font-medium ${allPass ? "text-emerald-700" : "text-rose-700"}`}>
                {allPass
                  ? "✓ This lead would be accepted by these criteria."
                  : "✗ This lead would be rejected — at least one rule fails."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────  Iter97 — "Copy as code" exporter  ───────────────────────── */

function CopyAsCodeButton({ draft, sector, testid }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("json");
  const [copiedKey, setCopiedKey] = useState(null);

  const snippets = useMemo(() => buildCodeSnippets(draft, sector), [draft, sector]);

  const copyText = async (key, txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedKey(key);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedKey(null), 1600);
    } catch (_) {
      toast.error("Copy failed — your browser blocked clipboard");
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        className="border-slate-200 gap-1.5"
        data-testid={`criteria-copy-as-code-${testid}`}
      >
        <Code2 className="size-3.5" />
        Copy as code
      </Button>

      <CodeDialog open={open} onOpenChange={setOpen}>
        <CodeDialogContent className="max-w-2xl">
          <CodeDialogHeader>
            <CodeDialogTitle className="text-lg flex items-center gap-2">
              <Code2 className="size-4 text-violet-500" />
              Export criteria as code
            </CodeDialogTitle>
            <p className="text-[12.5px] text-slate-500 mt-1 leading-snug">
              Share with your team, paste into Postman, or hand to your CRM dev. The snippets below describe an example lead that would <strong>match these criteria</strong>.
            </p>
            <CodeDialogDescription className="sr-only">
              Export the current criteria rules as a copy-paste code snippet in your preferred format.
            </CodeDialogDescription>
          </CodeDialogHeader>

          <Tabs value={tab} onValueChange={setTab} className="mt-2">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="json" data-testid="copy-tab-json">Rules JSON</TabsTrigger>
              <TabsTrigger value="curl" data-testid="copy-tab-curl">curl</TabsTrigger>
              <TabsTrigger value="node" data-testid="copy-tab-node">Node SDK</TabsTrigger>
              <TabsTrigger value="python" data-testid="copy-tab-python">Python</TabsTrigger>
            </TabsList>

            {Object.entries(snippets).map(([key, snip]) => (
              <TabsContent key={key} value={key} className="mt-3">
                <CodeBlock
                  text={snip}
                  language={key}
                  copied={copiedKey === key}
                  onCopy={() => copyText(key, snip)}
                  testid={`copy-block-${key}`}
                />
                <div className="mt-2 text-[11.5px] text-slate-500 leading-snug">
                  {key === "json"   && "Raw rules array — paste straight into the API or your own config store."}
                  {key === "curl"   && "Sample ingest call using a publisher API key. The example payload satisfies every rule above."}
                  {key === "node"   && "Node.js — fetch-based. Use the official @connexis/sdk for retries, telemetry, and types (download from /docs)."}
                  {key === "python" && "Python — requests-based. Use connexis-sdk on PyPI for retries + typed responses (download from /docs)."}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CodeDialogContent>
      </CodeDialog>
    </>
  );
}

function CodeBlock({ text, copied, onCopy, testid }) {
  return (
    <div className="relative rounded-md border border-slate-800 bg-slate-950 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={onCopy}
        className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 rounded h-7 px-2.5 bg-slate-800/80 hover:bg-slate-700 text-[11px] font-semibold text-slate-100 transition-colors z-10"
        data-testid={`${testid}-button`}
      >
        {copied ? <Check className="size-3 text-emerald-400" /> : <Copy className="size-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
      <pre className="font-mono text-[11.5px] leading-[1.55] text-slate-100 p-4 pr-24 overflow-x-auto max-h-72 m-0 whitespace-pre">{text}</pre>
    </div>
  );
}

// Build all four snippets. Each describes both the criteria themselves AND
// a sample lead payload that would satisfy every rule — so the buyer's
// integration partner has copy-paste-ready code AND knows what shape of
// lead the rules expect.
function buildCodeSnippets(draft, sector) {
  const rulesJson = JSON.stringify(draft, null, 2);

  // Build a sample lead by working backwards from the rules.
  const samplePayload = synthesizePassingLead(draft, sector);
  const samplePretty = JSON.stringify(samplePayload, null, 2);
  const sampleOneline = JSON.stringify(samplePayload);

  return {
    json: rulesJson,

    curl:
`# This lead would PASS every rule
curl -X POST https://api.connex.is/api/v1/leads/ingest \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: pk_live_••••••••" \\
  -d '${sampleOneline}'`,

    node:
`// npm i node-fetch    (or use built-in fetch on Node 18+)
const ingest = async (lead) => {
  const r = await fetch("https://api.connex.is/api/v1/leads/ingest", {
    method:  "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key":    process.env.CONNEXIS_API_KEY,   // pk_live_•••
    },
    body: JSON.stringify(lead),
  });
  return r.json();   // -> { accepted, lead_id, price_gbp, ... }
};

// Sample lead that satisfies the current campaign criteria:
const sample = ${samplePretty};

ingest(sample).then(console.log);`,

    python:
`# pip install requests
import os, requests

def ingest(lead):
    r = requests.post(
        "https://api.connex.is/api/v1/leads/ingest",
        headers={
            "Content-Type": "application/json",
            "X-API-Key":    os.environ["CONNEXIS_API_KEY"],   # pk_live_•••
        },
        json=lead,
        timeout=10,
    )
    return r.json()   # -> { accepted, lead_id, price_gbp, ... }

# Sample lead that satisfies the current campaign criteria:
sample = ${samplePretty.replace(/^/gm, "")}

print(ingest(sample))`,
  };
}

// Reverse-engineer a lead payload from the rules so the snippet's sample
// always passes. Best-effort — exotic rules (in/not_in with empty arrays)
// fall back to a neutral placeholder.
function synthesizePassingLead(draft, sector) {
  const base = {
    sector: sector || "mortgages",
    first_name: "Olivia",
    last_name: "Hughes",
    email: "olivia.hughes@example.com",
    phone: "+447900654321",
    country: "GB",
    extra_fields: { age: 36, annual_income: 48000, loan_amount: 185000 },
  };
  const setField = (key, val) => {
    const ROOT = ["sector", "first_name", "last_name", "email", "phone", "city", "postcode", "country", "region"];
    if (ROOT.includes(key)) base[key] = val;
    else base.extra_fields[key] = val;
  };
  for (const r of draft || []) {
    if (!r.field || !r.op) continue;
    switch (r.op) {
      case "equals":     setField(r.field, r.value); break;
      case "in":         if (Array.isArray(r.value) && r.value.length) setField(r.field, r.value[0]); break;
      case "gte": case "gt": setField(r.field, Number(r.value) + (r.op === "gt" ? 1 : 0)); break;
      case "lte": case "lt": setField(r.field, Number(r.value) - (r.op === "lt" ? 1 : 0)); break;
      case "exists":     if (base.extra_fields[r.field] === undefined && !(r.field in base)) setField(r.field, "value"); break;
      case "not_exists": delete base.extra_fields[r.field]; break;
      case "not_equals": setField(r.field, "different-value"); break;
      case "not_in":     setField(r.field, "outside-list-value"); break;
      default: break;
    }
  }
  return base;
}
